import { AxiosError, AxiosResponse } from "axios";
import { useContext } from "react";
import { useMutation, UseMutationResult, useQueryClient } from "react-query";
import { log } from "common/Logger";
import { useErrorLogger } from "common/LoggerHooks";
import { Antrag, AntragResponse, AntragsTyp, EditGastAntrag, GastAntrag, UploadDokumentResult } from "interfaces/Application";
import { ApiContext } from "services/ApiContext";
import { format } from "date-fns";
import { NIL as EmptyGuid } from "uuid";

export enum FileType {
  Meisterbrief = "Meisterbrief",
  Sicherheit = "Sicherheit",
  TGRI = "TGRI",
  Sonstige = "Sonstige",
}

export enum ApplicationFileType {
  Betriebshaftplicht = "Betriebshaftplicht",
  Gewerbeanmeldung = "Gewerbeanmeldung",
  Handwerksrolleneintragung = "Handwerksrolleneintragung",
  AusweisVorderSeite = "AusweisVorderseite",
  AusweisRueckseite = "AusweisRueckseite",
}

export const useCreateApplication = (): UseMutationResult<string, AxiosError, Antrag> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const { mutateAsync: moveDokumenteToCRM } = useMoveDokumenteToCRM();

  const CreateApplication = async (data: Antrag) => {
    log.trace({ obj: data }, "creating new Antrag");
    
    if (!data.installationsUnternehmen.istKreishandwerkerschaft) data.installationsUnternehmen.istKreishandwerkerschaft = false;

    const response = await api.post<Antrag, AxiosResponse<AntragResponse>>("/antraege", data);
    log.trace({ obj: response }, "result from post application");
    const ApplicationId = response.data.applicationid;
    const newInstallationsUnternehmenId = response.data.installationsUnternehmenId;
    if(data.installationsUnternehmenId === "") data.installationsUnternehmenId = EmptyGuid;

    // wenn bei den Fachkräften Dateien angefügt waren, dann werden die zum jeweiligen Unternehmen hochgeladen
    // Subject sollte folgendermaßen aussehen für ein Fachkraft Dokument. "data.fachkraftName:Upload_Dokument_Typ"
    const moveFiles: MoveDokument[] = [];
    data.fachkraefte.forEach((item) => {
      if (item.befaehigungDatei)
        moveFiles.push({ subject: item.befaehigungDatei.subject, documentId: item.befaehigungDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

      if (item.sicherheitsDatei)
        moveFiles.push({ subject: item.sicherheitsDatei.subject, documentId: item.sicherheitsDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

      if (item.tgriDatei) moveFiles.push({ subject: item.tgriDatei.subject, documentId: item.tgriDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

      if (item.sonstigeDatei)
        moveFiles.push({ subject: item.sonstigeDatei.subject, documentId: item.sonstigeDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });
    });

    // Subject sollte folgendermaßen aussehen für ein Firmen/Antrag Dokument "data.firmenName:Upload_Dokument_Typ"
    if (data.betriebsHaftplichtDatei)
      moveFiles.push({
        subject: data.betriebsHaftplichtDatei.subject,
        documentId: data.betriebsHaftplichtDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId,
        newInstallationsUnternehmenId: newInstallationsUnternehmenId,
      });

    if (data.gewerbeAnmeldungDatei)
      moveFiles.push({
        subject: data.gewerbeAnmeldungDatei.subject,
        documentId: data.gewerbeAnmeldungDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId,
        newInstallationsUnternehmenId: newInstallationsUnternehmenId,
      });

    if (data.handwerksrollenEintragungDatei)
      moveFiles.push({
        subject: data.handwerksrollenEintragungDatei.subject,
        documentId: data.handwerksrollenEintragungDatei.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId,
        newInstallationsUnternehmenId: newInstallationsUnternehmenId,
      });

    await moveDokumenteToCRM(moveFiles);

    return ApplicationId;
  };

  return useMutation<string, AxiosError, Antrag>(CreateApplication, {
    onSuccess: (_d, input) => {
      queryClient.invalidateQueries("Applications");

      // wenn erstantrag, dann firmendaten neu laden
      if (input.antragstyp === AntragsTyp.ERSTANMELDUNG) queryClient.invalidateQueries("Companies");

      log.debug("successfully created new application");
    },
    onError: (err) => logAxiosError("Error saving application", "Fehler beim Speichern des Antrags", err),
  });
};

export const useCreateGastApplication = (): UseMutationResult<string, AxiosError, GastAntrag> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const { mutateAsync: moveDokumenteToCRM } = useMoveDokumenteToCRM();

  const CreateApplication = async (data: GastAntrag) => {
    log.trace({ obj: data }, "creating new GastAntrag");

    const konzessionsGebietId = data.konzessionsGebietId.startsWith("new-id-") ? "" : data.konzessionsGebietId;

    const postData = {
      ...data,
      konzessionsGebietId,
      ausweisGueltigkeit: format(new Date(data.ausweisGueltigkeit), "yyyy-MM-dd"),
    };

    const response = await api.post<GastAntrag, AxiosResponse<AntragResponse>>("/gastinstallteur", postData);
    log.trace({ obj: response }, "result from post application");
    const ApplicationId = response.data.applicationid;
    const newInstallationsUnternehmenId = response.data.installationsUnternehmenId;
    if(data.installationsUnternehmenId === "") data.installationsUnternehmenId = EmptyGuid;

    // wenn bei den Fachkräften Dateien angefügt waren, dann werden die zum jeweiligen Unternehmen hochgeladen
    // Subject sollte folgendermaßen aussehen für ein Fachkraft Dokument. "data.fachkraftName:Upload_Dokument_Typ"
    const moveFiles: MoveDokument[] = [];

    // Subject sollte folgendermaßen aussehen für ein Firmen/Antrag Dokument "data.firmenName:Upload_Dokument_Typ"
    if (data.ausweisRueckseite)
      moveFiles.push({ subject: data.ausweisRueckseite.subject, documentId: data.ausweisRueckseite.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

    if (data.ausweisVorderseite)
      moveFiles.push({ subject: data.ausweisVorderseite.subject, documentId: data.ausweisVorderseite.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

    await moveDokumenteToCRM(moveFiles);

    return ApplicationId;
  };

  return useMutation<string, AxiosError, GastAntrag>(CreateApplication, {
    onSuccess: () => {
      queryClient.invalidateQueries("Applications");
      log.debug("successfully created new application");
    },
    onError: (err) => logAxiosError("Error saving guest application", "Fehler beim Speichern des GastAntrags", err),
  });
};

export const useUpdateGastAusweisApplication = (): UseMutationResult<string, AxiosError, EditGastAntrag> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const { mutateAsync: moveDokumenteToCRM } = useMoveDokumenteToCRM();

  const UpdateApplication = async (data: EditGastAntrag) => {
    log.trace({ obj: data }, "updating EditGastAntrag");

    const response = await api.post<EditGastAntrag, AxiosResponse<AntragResponse>>("/gastinstallteur", data);
    log.trace({ obj: response }, "result from put application");
    const ApplicationId = response.data.applicationid;
    const newInstallationsUnternehmenId = response.data.installationsUnternehmenId;
    if(data.installationsUnternehmenId === "") data.installationsUnternehmenId = EmptyGuid;

    // wenn bei den Fachkräften Dateien angefügt waren, dann werden die zum jeweiligen Unternehmen hochgeladen
    // Subject sollte folgendermaßen aussehen für ein Fachkraft Dokument. "data.fachkraftName:Upload_Dokument_Typ"
    const moveFiles: MoveDokument[] = [];

    // Subject sollte folgendermaßen aussehen für ein Firmen/Antrag Dokument "data.firmenName:Upload_Dokument_Typ"
    if (data.ausweisRueckseite)
      moveFiles.push({ subject: data.ausweisRueckseite.subject, documentId: data.ausweisRueckseite.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

    if (data.ausweisVorderseite)
      moveFiles.push({ subject: data.ausweisVorderseite.subject, documentId: data.ausweisVorderseite.documentId, applicationid: ApplicationId, installationsUnternehmenId: data.installationsUnternehmenId, newInstallationsUnternehmenId: newInstallationsUnternehmenId });

    await moveDokumenteToCRM(moveFiles);

    return ApplicationId;
  };

  return useMutation<string, AxiosError, EditGastAntrag>(UpdateApplication, {
    onSuccess: () => {
      queryClient.invalidateQueries("Applications");
      log.debug("successfully updated new application");
    },
    onError: (err) => logAxiosError("Error saving guest application", "Fehler beim Speichern des GastAntrags", err),
  });
};

export const useUpdateGastMainDataApplication = (): UseMutationResult<string, AxiosError, EditGastAntrag> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const UpdateApplication = async (data: EditGastAntrag) => {
    log.trace({ obj: data }, "updating EditGastAntrag");
    const konzessionsGebietId = data.konzessionsGebietId.startsWith("new-id-") ? "" : data.konzessionsGebietId;
    const postData: EditGastAntrag = {
      ...data,
      konzessionsGebietId,
    };

    const response = await api.post<EditGastAntrag, AxiosResponse<AntragResponse>>("/gastinstallteur", postData);
    log.trace({ obj: response }, "result from put application");
    const ApplicationId = response.data.applicationid;

    return ApplicationId;
  };

  return useMutation<string, AxiosError, EditGastAntrag>(UpdateApplication, {
    onSuccess: () => {
      queryClient.invalidateQueries("Applications");
      log.debug("successfully updated new application");
    },
    onError: (err) => logAxiosError("Error saving guest application", "Fehler beim Speichern des GastAntrags", err),
  });
};

const base64RegExp = /data:\w*\/[\w.-]*;base64,/gm;

interface AntragDokument {
  installationsUnternehmenId: string;
  datei: File;
  subject: string;
}

interface MoveDokument {
  installationsUnternehmenId: string;
  newInstallationsUnternehmenId: string;
  applicationid: string;
  subject: string;
  documentId: string;
}

interface UploadDokument {
  installationsUnternehmenId: string;
  subject: string;
  filename: string;
  documentbody: string;
}

export const useUploadDokumente = (): UseMutationResult<UploadDokumentResult, AxiosError, AntragDokument> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  const UploadDokumente = async (data: AntragDokument) => {
    log.trace({ obj: data }, "uploading new dokumente");

    const base64String = (await toBase64(data.datei)).replace(base64RegExp, "");
    const uploadDoc: UploadDokument = {
      installationsUnternehmenId: data.installationsUnternehmenId ? data.installationsUnternehmenId : EmptyGuid,
      documentbody: base64String,
      filename: data.datei.name,
      subject: data.subject,
    };

    const response = await api.post(`/dokumente`, [uploadDoc]);
    log.trace({ obj: response }, "result from post dokumente");
    const result = response.data;
    return result;
  };

  return useMutation<UploadDokumentResult, AxiosError, AntragDokument>(UploadDokumente, {
    onSuccess: () => {
      queryClient.invalidateQueries("Licenses");
      log.debug("successfully uploaded new dokumente");
    },
    onError: (err) => logAxiosError("Error uploading file", "Fehler beim Speichern der Datei(en)", err),
  });
};

export const useMoveDokumenteToCRM = (): UseMutationResult<string, AxiosError, MoveDokument[]> => {
  const { logAxiosError } = useErrorLogger();
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const MoveDokumenteToCRM = async (data: MoveDokument[]) => {
    log.trace({ obj: data }, "moving dokumente to CRM");

    const response = await api.put(`/dokumente`, data);
    log.trace({ obj: response }, "result from put dokumente");
    const result = response.data;
    return result;
  };

  return useMutation<string, AxiosError, MoveDokument[]>(MoveDokumenteToCRM, {
    onSuccess: () => {
      queryClient.invalidateQueries("Licenses");
      log.debug("successfully moved dokumente");
    },
    onError: (err) => logAxiosError("Error moving file", "Fehler beim Übertragen der Datei(en)", err),
  });
};
